import {
  Link,
  logOut,
  placeholder,
  useAuthStore,
  useCharity,
  ThemeSwitcher,
  MoreMenu,
} from "@/index.js";
import { memo } from "react";
import RevalidateAll from "./RevalidateAll";
import WhatsNew from "@/shared/components/navBar/WhatsNew.jsx";

const Options = () => {
  const { user } = useAuthStore();
  const { charityStoreUrl } = useCharity();

  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      {/* Secondary utilities — inline on sm+, collapsed into MoreMenu below sm */}
      <div className="hidden items-center gap-1.5 sm:flex sm:gap-2">
        {/* Theme switcher — System / Light / Dark */}
        <ThemeSwitcher />
        <RevalidateAll />
      </div>

      {/* Always visible — carries the unread badge */}
      <WhatsNew />

      {/* Divider */}
      <span
        aria-hidden
        className="hidden h-6 w-px bg-gray-200 dark:bg-gray-700 sm:block"
      />

      {/* Store CTA — compact pill, sm+ only (mobile lives in MoreMenu) */}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={charityStoreUrl}
        title="عرض المتجر"
        className="group hidden h-10 items-center gap-2 rounded-full px-3 text-sm font-semibold text-gray-500 ring-1 ring-gray-200 transition-all duration-200 hover:bg-white hover:ring-purple-200 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-900/50 dark:hover:ring-purple-700/40 sm:inline-flex"
      >
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          className="transition-transform group-hover:scale-110"
        >
          <path
            d="M16.6667 2.16675H3.33341C2.89139 2.16675 2.46746 2.34234 2.1549 2.6549C1.84234 2.96746 1.66675 3.39139 1.66675 3.83341V13.0001C1.66675 13.4421 1.84234 13.866 2.1549 14.1786C2.46746 14.4912 2.89139 14.6667 3.33341 14.6667H9.16675V17.1667H6.66675C6.44573 17.1667 6.23377 17.2545 6.07749 17.4108C5.92121 17.5671 5.83342 17.7791 5.83342 18.0001C5.83342 18.2211 5.92121 18.4331 6.07749 18.5893C6.23377 18.7456 6.44573 18.8334 6.66675 18.8334H13.3334C13.5544 18.8334 13.7664 18.7456 13.9227 18.5893C14.079 18.4331 14.1667 18.2211 14.1667 18.0001C14.1667 17.7791 14.079 17.5671 13.9227 17.4108C13.7664 17.2545 13.5544 17.1667 13.3334 17.1667H10.8334V14.6667H16.6667C17.1088 14.6667 17.5327 14.4912 17.8453 14.1786C18.1578 13.866 18.3334 13.4421 18.3334 13.0001V3.83341C18.3334 3.39139 18.1578 2.96746 17.8453 2.6549C17.5327 2.34234 17.1088 2.16675 16.6667 2.16675ZM16.6667 3.83341V9.66675H3.33341V3.83341H16.6667ZM3.33341 13.0001V11.3334H16.6667V13.0001H3.33341Z"
            fill="currentColor"
          />
        </svg>

        <span className="leading-none">عرض المنصة</span>
      </a>

      {/* Overflow menu — mobile only (collects theme / revalidate / store) */}
      <MoreMenu />

      {/* Profile */}
      <div className="relative flex items-center">
        <button
          type="button"
          aria-expanded="false"
          aria-label="القائمة الشخصية"
          data-dropdown-toggle="dropdown-profile"
          className="group relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-700 to-purple-950 text-white shadow-sm shadow-purple-900/20 ring-2 ring-white dark:ring-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <span className="text-sm font-bold uppercase tracking-wide">
            {user?.displayName?.substring(0, 2)}
          </span>
          {/* Online status dot */}
          <span className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
        </button>

        <div
          id="dropdown-profile"
          className="z-[60] my-3 hidden w-72 list-none overflow-hidden rounded-2xl border border-gray-100 bg-white/95 text-base shadow-xl shadow-gray-900/10 backdrop-blur-xl dark:border-gray-700/60 dark:bg-gray-900/95 dark:shadow-black/40 dark:text-gray-100"
        >
          {/* Header */}
          <div className="relative overflow-hidden border-b border-gray-100 bg-gradient-to-br from-purple-50 via-white to-purple-50/40 px-4 py-4 dark:border-gray-800 dark:from-purple-950/50 dark:via-gray-900 dark:to-purple-950/30 dark:bg-gray-900/50">
            <div className="relative flex items-center gap-3">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-white/30 dark:from-purple-600 dark:to-purple-800">
                <span className="text-base font-bold uppercase tracking-wide">
                  {user?.displayName?.substring(0, 2)}
                </span>
              </span>
              <div className="min-w-0">
                <h3 className="truncate text-sm font-bold tracking-tight text-gray-900 dark:text-gray-300">
                  {user?.displayName}
                </h3>
                <p className="truncate text-xs text-gray-500 dark:text-gray-300">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu */}
          <ul className="space-y-0.5 p-2" role="none">
            <li>
              <Link
                to="/profile"
                role="menuitem"
                className="group/item flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-gray-700 transition-colors hover:bg-purple-50 hover:text-purple-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-purple-300 dark:hover:ring-purple-700/40 dark:hover:ring-1"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700">
                  <img
                    alt="avatar"
                    loading="lazy"
                    src={placeholder}
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="flex-1 text-sm font-medium tracking-tight">
                  عرض الملف الشخصي
                </span>
                <svg
                  className="h-4 w-4 -translate-x-1 opacity-0 transition-all group-hover/item:translate-x-0 group-hover/item:opacity-100 rtl:rotate-180"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </li>

            <li>
              <span
                role="menuitem"
                onClick={() => logOut()}
                className="group/item flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-red-950/40 dark:hover:text-red-400"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500 ring-1 ring-red-100 transition-colors dark:bg-red-950/40 dark:text-red-400 dark:ring-red-900/40">
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 18 19"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.25 13.5l3.75-3.75m0 0L11.25 6m3.75 3.75H6m-.75 5.25H4.5a1.5 1.5 0 01-1.5-1.5V5.25a1.5 1.5 0 011.5-1.5h.75"
                    />
                  </svg>
                </span>
                <span className="flex-1 text-sm font-medium tracking-tight">
                  تسجيل خروج
                </span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default memo(Options);
